import { OpenAPI } from "@/client"

const DEMO_MIRROR_KEY = "demo_mode"
const TOKEN_KEY = "access_token"
const BANNER_PATH = "/api/v1/demo/banner"
const VISITOR_SESSION_PATH = "/api/v1/demo/visitor-session"

export type DemoBanner = {
  enabled: boolean
  message_key?: string
  // Free-text slots flanking the HH:MM:SS countdown. ``null``/``undefined``
  // means "use the SPA's i18n default for this slot" so operators can
  // clear an override by saving an empty string in /admin-demo.
  banner_left_message?: string | null
  banner_right_message?: string | null
  purge_interval_hours?: number
  last_purge_at?: string | null
  next_purge_at?: string | null
}

function apiUrl(path: string): string {
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return `${base}${path}`
}

/**
 * Synchronous read of the most recently-seen demo flag.
 *
 * Used by the TanStack Router beforeLoad guard (which runs synchronously)
 * so we can decide between /login redirect and visitor-session bootstrap
 * without an `await`. The flag is refreshed by useDemoMode().
 */
export function isDemoEnabledSync(): boolean {
  try {
    return localStorage.getItem(DEMO_MIRROR_KEY) === "true"
  } catch {
    return false
  }
}

export function setDemoMirror(enabled: boolean): void {
  try {
    localStorage.setItem(DEMO_MIRROR_KEY, enabled ? "true" : "false")
  } catch {
    /* ignore */
  }
}

/** One-shot fetch of /demo/banner — used to seed the mirror on cold start. */
export async function fetchDemoBanner(): Promise<DemoBanner> {
  const res = await fetch(apiUrl(BANNER_PATH))
  if (!res.ok) {
    return { enabled: false }
  }
  const data = (await res.json()) as DemoBanner
  setDemoMirror(!!data?.enabled)
  return data
}

/**
 * Mint a visitor JWT and store it in localStorage.
 *
 * Throws when the cluster isn't in demo mode or the visitor cap is
 * reached. The caller (login screen or beforeLoad bypass) is
 * responsible for handling failures gracefully — they can surface
 * the error to the user and redirect to /login.
 */
export async function mintVisitorSession(): Promise<string> {
  const res = await fetch(apiUrl(VISITOR_SESSION_PATH), { method: "POST" })
  if (!res.ok) {
    const body = await res.text().catch(() => "")
    throw new Error(
      `Failed to mint visitor session (HTTP ${res.status}): ${body}`,
    )
  }
  const data = (await res.json()) as { access_token: string }
  try {
    localStorage.setItem(TOKEN_KEY, data.access_token)
  } catch {
    /* ignore */
  }
  return data.access_token
}
